/**
 * LinkedIn field limits and truncation targets — all in one place.
 *
 * LinkedIn owns these numbers and changes them without notice, so they are
 * collected here to make updating any of them a one-line change. If you notice
 * one has drifted, edit it here and nothing else. See `docs/ADDING_A_RULE.md`.
 *
 * Two kinds of number live here, and the distinction matters:
 *
 * - `hard`   — enforced by LinkedIn's own editor; it stops you typing. Exceeding
 *              one means the text was pasted from elsewhere and *will* be cut.
 * - targets  — our recommendations, derived from where LinkedIn's UI visually
 *              truncates text. Truncation is responsive (it differs between
 *              desktop, mobile, and search results), so these are guidance and
 *              rules should treat them as soft gradients, not cliffs.
 */
export const LIMITS = {
  headline: {
    /** Hard cap in LinkedIn's headline editor. */
    hard: 220,
    /** Below this there is not enough room to say anything useful. */
    minRecommended: 40,
    /** Roughly what survives in search results and on mobile. Front-load this. */
    previewTarget: 60,
  },

  about: {
    /** Hard cap in the About editor. */
    hard: 2600,
    /** Below this the section reads as an afterthought. */
    minRecommended: 400,
    /** Roughly what shows before the "…see more" fold. Your hook lives here. */
    seeMoreTarget: 265,
    /** Above this length without a blank line, it reads as a wall of text. */
    wallOfTextThreshold: 800,
    /** Window at the end of the text we search for a call to action. */
    ctaWindow: 300,
  },

  roleDescription: {
    /** Hard cap per experience entry. */
    hard: 2000,
  },

  skills: {
    /** Hard cap on skills per profile. */
    hard: 50,
    /** How many appear as "top skills" without the reader expanding. */
    pinned: 3,
    /** Below this, skill-based search barely surfaces the profile. */
    minRecommended: 5,
    /** A comfortably complete skill list. */
    goodCount: 15,
  },

  bullet: {
    /** Past this, a bullet stops being skimmable. */
    maxWords: 35,
    /** Fewest bullets that can describe a role credibly. */
    minPerRole: 2,
  },

  experience: {
    /** Share of bullets that should carry a number to read as evidence-backed. */
    quantifiedRatioTarget: 0.3,
  },

  recommendations: {
    minRecommended: 2,
  },

  keywords: {
    /** Target-role keyword coverage below which the profile reads as off-topic. */
    coverageTarget: 0.3,
  },
} as const;
